import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Phone, MapPin } from "lucide-react";
import PageHero from "@/components/shared/PageHero";
import { useLang } from "@/i18n/LanguageContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoordCard = ({ Icon, label, lines, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
    data-testid={testId}
    className="p-8 md:p-10 border-r border-b border-ekip-gold/20 bg-white hover:bg-ekip-cream transition-colors"
  >
    <Icon size={22} strokeWidth={1.4} className="text-ekip-copper" />
    <span className="mt-8 block text-[10px] uppercase tracking-[0.3em] text-ekip-copper font-medium">
      {label}
    </span>
    <div className="mt-3 space-y-1">
      {lines.map((l) => (
        <p key={l} className="font-display font-semibold text-lg md:text-xl text-ekip-navy">
          {l}
        </p>
      ))}
    </div>
  </motion.div>
);

export default function Contact() {
  const { t } = useLang();
  const [general, setGeneral] = useState({ name: "", email: "", subject: "", message: "" });
  const [investor, setInvestor] = useState({ name: "", email: "", organization: "", role: "", investorType: "", note: "" });
  const [loadingG, setLoadingG] = useState(false);
  const [loadingI, setLoadingI] = useState(false);

  const submitGeneral = async (e) => {
    e.preventDefault();
    setLoadingG(true);
    try {
      await axios.post(`${API}/contact`, general);
      toast.success(t.common.success);
      setGeneral({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error(t.common.error);
    } finally {
      setLoadingG(false);
    }
  };

  const submitInvestor = async (e) => {
    e.preventDefault();
    setLoadingI(true);
    try {
      await axios.post(`${API}/investors`, investor);
      toast.success(t.common.success);
      setInvestor({ name: "", email: "", organization: "", role: "", investorType: "", note: "" });
    } catch {
      toast.error(t.common.error);
    } finally {
      setLoadingI(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        overline={t.contact.hero.overline}
        title={t.contact.hero.title}
        titleItalic={t.contact.hero.titleItalic}
        subtitle={t.contact.hero.subtitle}
      />

      {/* General form */}
      <section className="bg-ekip-cream py-20 md:py-24" data-testid="contact-general">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-14">
            <div className="col-span-12 md:col-span-4">
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium flex items-center gap-3">
                <span className="diamond" />
                {t.contact.general.eyebrow}
              </span>
              <h2 className="mt-6 font-display font-semibold text-3xl md:text-4xl leading-[1.05] text-ekip-navy">
                {t.contact.general.title}
              </h2>
            </div>
            <form onSubmit={submitGeneral} className="col-span-12 md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-testid="contact-general-form">
              <input
                required
                type="text"
                placeholder={t.contact.general.name}
                value={general.name}
                onChange={(e) => setGeneral({ ...general, name: e.target.value })}
                data-testid="contact-input-name"
                className="form-input"
              />
              <input
                required
                type="email"
                placeholder={t.contact.general.email}
                value={general.email}
                onChange={(e) => setGeneral({ ...general, email: e.target.value })}
                data-testid="contact-input-email"
                className="form-input"
              />
              <div className="md:col-span-2">
                <select
                  required
                  value={general.subject}
                  onChange={(e) => setGeneral({ ...general, subject: e.target.value })}
                  data-testid="contact-input-subject"
                  className="form-input bg-transparent"
                >
                  <option value="" disabled>{t.contact.general.subject}</option>
                  {t.contact.general.subjects.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <textarea
                  required
                  rows={4}
                  placeholder={t.contact.general.message}
                  value={general.message}
                  onChange={(e) => setGeneral({ ...general, message: e.target.value })}
                  data-testid="contact-input-message"
                  className="form-input resize-none"
                />
              </div>
              <div className="md:col-span-2 mt-2">
                <button
                  type="submit"
                  disabled={loadingG}
                  data-testid="contact-submit"
                  className="btn-primary disabled:opacity-60"
                >
                  {loadingG ? t.common.submitting : t.common.submit}
                  <ArrowRight size={14} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Investors form */}
      <section className="bg-ekip-navy py-20 md:py-24" data-testid="contact-investors">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-14">
            <div className="col-span-12 md:col-span-4">
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium flex items-center gap-3">
                <span className="diamond" />
                {t.contact.investors.eyebrow}
              </span>
              <h2 className="mt-6 font-display font-semibold text-3xl md:text-4xl leading-[1.05] text-white">
                {t.contact.investors.title}
                <br />
                <em className="italic font-light text-ekip-copper">{t.contact.investors.titleItalic}</em>
              </h2>
              <p className="mt-6 text-white/70 font-body font-light leading-relaxed">
                {t.contact.investors.subtitle}
              </p>
            </div>
            <form onSubmit={submitInvestor} className="col-span-12 md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-testid="investor-form">
              <input required type="text" placeholder={t.contact.general.name}
                value={investor.name}
                onChange={(e) => setInvestor({ ...investor, name: e.target.value })}
                data-testid="investor-input-name"
                className="form-input form-input-dark"
              />
              <input required type="email" placeholder={t.contact.general.email}
                value={investor.email}
                onChange={(e) => setInvestor({ ...investor, email: e.target.value })}
                data-testid="investor-input-email"
                className="form-input form-input-dark"
              />
              <input required type="text" placeholder={t.contact.investors.organization}
                value={investor.organization}
                onChange={(e) => setInvestor({ ...investor, organization: e.target.value })}
                data-testid="investor-input-org"
                className="form-input form-input-dark"
              />
              <input type="text" placeholder={t.contact.investors.role}
                value={investor.role}
                onChange={(e) => setInvestor({ ...investor, role: e.target.value })}
                data-testid="investor-input-role"
                className="form-input form-input-dark"
              />
              <div className="md:col-span-2">
                <select
                  required
                  value={investor.investorType}
                  onChange={(e) => setInvestor({ ...investor, investorType: e.target.value })}
                  data-testid="investor-input-type"
                  className="form-input form-input-dark bg-ekip-navy"
                >
                  <option value="" disabled>{t.contact.investors.investorType}</option>
                  {t.contact.investors.types.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <textarea rows={4} placeholder={t.contact.investors.note}
                  value={investor.note}
                  onChange={(e) => setInvestor({ ...investor, note: e.target.value })}
                  data-testid="investor-input-note"
                  className="form-input form-input-dark resize-none"
                />
              </div>
              <div className="md:col-span-2 mt-2">
                <button type="submit" disabled={loadingI} data-testid="investor-submit" className="btn-primary disabled:opacity-60">
                  {loadingI ? t.common.submitting : t.common.submit}
                  <ArrowRight size={14} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Coordinates */}
      <section className="bg-white py-20 md:py-24 border-t border-ekip-gold/20" data-testid="contact-coords">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.contact.coords.eyebrow}
              </span>
            </div>
            <h2 className="col-span-12 md:col-span-8 font-display font-semibold text-3xl md:text-5xl leading-[1.05] text-ekip-navy">
              {t.contact.coords.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-ekip-gold/20">
            <CoordCard Icon={MapPin} label={t.contact.coords.addressLabel} lines={t.contact.coords.address} testId="coord-address" />
            <CoordCard Icon={Mail} label={t.contact.coords.emailLabel} lines={t.contact.coords.emails} testId="coord-email" />
            <CoordCard Icon={Phone} label={t.contact.coords.phoneLabel} lines={t.contact.coords.phones} testId="coord-phone" />
          </div>
        </div>
      </section>
    </div>
  );
}
